import React, { Component } from 'react'
import { connect } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Spin,
  Upload
} from 'antd'
import moment from 'moment'

import axios from 'util/Api'
import { fetchBowserAssets } from '../../appRedux/actions/Asset'

const { TextArea } = Input

class Create extends Component {
  state = {
    bowser: undefined,
    datetime: moment(),
    adjustmentType: 'INCREASE',
    quantity: null,
    remark: '',
    attachments: [],
    fileList: [],
    uploading: false,
    negativeWarning: '',
    checkingBalance: false
  }

  componentDidMount () {
    this.props.fetchBowserAssets()
  }

  // Warn (but allow) when a decrease would drive the bowser balance negative.
  checkNegativeBalance = () => {
    const { bowser, datetime, adjustmentType, quantity } = this.state
    if (!bowser || !datetime || !quantity || adjustmentType !== 'DECREASE') {
      this.setState({ negativeWarning: '' })
      return
    }
    this.setState({ checkingBalance: true })
    axios.post('/bowser-fuel-transactions/check-negative-balance', {
      bowserId: bowser,
      datetime: moment(datetime).valueOf(),
      quantity: -Math.abs(quantity)
    }).then(response => {
      const data = response && response.data
      let warning = ''
      if (data && data.isValid === false) {
        warning = data.message || 'This decrease will make the bowser balance negative.'
        // The backend embeds a UTC ISO timestamp – show it in the user's local timezone.
        if (data.negativeBalanceAt) {
          const localTime = moment(data.negativeBalanceAt).format('YYYY-MM-DD HH:mm')
          warning = warning.replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/, localTime)
        }
      }
      this.setState({
        checkingBalance: false,
        negativeWarning: warning
      })
    }).catch(() => {
      this.setState({ checkingBalance: false, negativeWarning: '' })
    })
  }

  handleBowserChange = (value) => {
    this.setState({ bowser: value }, this.checkNegativeBalance)
  }

  handleDateChange = (value) => {
    this.setState({ datetime: value }, this.checkNegativeBalance)
  }

  handleTypeChange = (e) => {
    this.setState({ adjustmentType: e.target.value }, this.checkNegativeBalance)
  }

  handleQuantityChange = (value) => {
    this.setState({ quantity: value }, this.checkNegativeBalance)
  }

  // Push each selected file to /common/upload-files and keep the returned S3 refs.
  customUpload = ({ file, onSuccess, onError }) => {
    const formData = new FormData()
    formData.append('files', file)
    this.setState({ uploading: true })
    axios.post('/common/upload-files', formData, { config: { headers: { 'Content-Type': 'multipart/form-data' } } })
      .then(response => {
        const uploaded = (response && response.data && response.data[0]) || {}
        const attachment = {
          name: uploaded.name || file.name,
          path: uploaded.url,
          type: uploaded.type || file.type,
          key: uploaded.key
        }
        this.setState(prev => ({
          uploading: false,
          attachments: [...prev.attachments, attachment]
        }))
        onSuccess(uploaded, file)
      })
      .catch(err => {
        this.setState({ uploading: false })
        message.error('File upload failed')
        onError(err)
      })
  }

  handleFileChange = ({ fileList }) => {
    this.setState({ fileList })
  }

  handleRemoveFile = (file) => {
    this.setState(prev => ({
      attachments: prev.attachments.filter(a => a.name !== file.name),
      fileList: prev.fileList.filter(f => f.uid !== file.uid)
    }))
  }

  handleSubmit = (e) => {
    e.preventDefault()
    this.props.form.validateFields((err, values) => {
      if (err) {
        return
      }
      if (this.state.uploading) {
        message.warning('Please wait for attachments to finish uploading.')
        return
      }
      if (this.state.negativeWarning) {
        message.error(this.state.negativeWarning)
        return
      }
      const payload = {
        bowser: values.bowser,
        datetime: moment(values.datetime).valueOf(),
        adjustmentType: values.adjustmentType,
        quantity: Math.abs(values.quantity),
        remark: values.remark,
        attachments: this.state.attachments
      }
      this.props.onSubmit(payload)
    })
  }

  render () {
    const { form, loading, bowsers } = this.props
    const { getFieldDecorator } = form
    const { negativeWarning, checkingBalance, uploading, fileList } = this.state

    return (
      <Spin spinning={loading}>
        <Card title={'Create Fuel Adjustment'}>
          <Form onSubmit={this.handleSubmit} layout={'vertical'}>
            <Form.Item label={'Date & Time'}>
              {getFieldDecorator('datetime', {
                initialValue: moment(),
                rules: [{ required: true, message: 'Please select a date and time' }]
              })(
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format={'YYYY-MM-DD HH:mm'}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  onChange={this.handleDateChange}
                />
              )}
            </Form.Item>

            <Form.Item label={'Bowser'}>
              {getFieldDecorator('bowser', {
                rules: [{ required: true, message: 'Please select a bowser' }]
              })(
                <Select
                  showSearch
                  placeholder={'Select a bowser'}
                  optionFilterProp={'children'}
                  filterOption={(input, option) =>
                    (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0}
                  onChange={this.handleBowserChange}
                >
                  {(bowsers || []).map(item => (
                    <Select.Option key={item._id} value={item._id}>
                      {item.assetCode ? `${item.assetCode}${item.name ? ' - ' + item.name : ''}` : (item.name || item._id)}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>

            <Form.Item label={'Adjustment'}>
              {getFieldDecorator('adjustmentType', {
                initialValue: 'INCREASE',
                rules: [{ required: true }]
              })(
                <Radio.Group onChange={this.handleTypeChange}>
                  <Radio.Button value={'INCREASE'}>Increase (+)</Radio.Button>
                  <Radio.Button value={'DECREASE'}>Decrease (-)</Radio.Button>
                </Radio.Group>
              )}
            </Form.Item>

            <Form.Item label={'Quantity (litres)'}>
              {getFieldDecorator('quantity', {
                rules: [
                  { required: true, message: 'Please enter a quantity' },
                  {
                    validator: (rule, value, callback) => {
                      if (value !== undefined && value !== null && value <= 0) {
                        callback('Quantity must be greater than 0')
                      } else {
                        callback()
                      }
                    }
                  }
                ]
              })(
                <InputNumber min={0} step={1} style={{ width: '100%' }} onChange={this.handleQuantityChange} />
              )}
            </Form.Item>

            {checkingBalance &&
              <div style={{ marginBottom: '12px' }}><Spin size={'small'} /> <span>Checking bowser balance…</span></div>}

            {negativeWarning &&
              <Alert
                style={{ marginBottom: '12px' }}
                type={'error'}
                showIcon
                message={negativeWarning}
                description={'This decrease is not allowed – it would make the bowser balance negative.'}
              />}

            <Form.Item label={'Remark'}>
              {getFieldDecorator('remark', {
                rules: [{ required: true, message: 'Please enter a remark' }]
              })(
                <TextArea rows={3} placeholder={'Reason for this adjustment'} />
              )}
            </Form.Item>

            <Form.Item label={'Attachments (photo / document)'}>
              <Upload
                multiple
                customRequest={this.customUpload}
                fileList={fileList}
                onChange={this.handleFileChange}
                onRemove={this.handleRemoveFile}
              >
                <Button>
                  <Icon type={'upload'} /> Upload
                </Button>
              </Upload>
            </Form.Item>

            <Form.Item>
              <Button type={'primary'} htmlType={'submit'} loading={loading} disabled={uploading}>
                CREATE
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    )
  }
}

const mapStateToProps = ({ auth, commonData, asset }) => {
  return {
    authUser: auth.authUser,
    loading: commonData.loading,
    bowsers: asset.bowsers
  }
}

const CreateForm = Form.create({ name: 'fuel_adjustment' })(Create)

export default connect(mapStateToProps, { fetchBowserAssets })(CreateForm)
